import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ShieldIcon from '@mui/icons-material/Shield';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import toast from 'react-hot-toast';
import { userAPI } from '../../../API/userAPI';
import { clientAPI } from '../../../API/clientAPI';
import { clientInitials } from '../../../Utils/format';
import { NOTIF_CATEGORIES } from '../../../Constants/userConstants';
import GatewayContagemContext from '../../../Context/GatewayContagemContext';
import AccessDeniedState from '../../../Components/GatewayContagem/AccessDeniedState';
import NotFoundState from '../../../Components/GatewayContagem/NotFoundState';
import { isForbiddenError, isNotFoundError } from '../../../Utils/apiHelpers';

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('users');

  const { clients, loadClients } = useContext(GatewayContagemContext);

  const [user, setUser] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isForbidden, setIsForbidden] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);

  // Notification preferences
  const [notifPrefs, setNotifPrefs] = useState({});
  const [notifLoading, setNotifLoading] = useState(false);

  const loadUserData = useCallback(async () => {
    setLoading(true);
    setIsForbidden(false);
    setIsNotFound(false);
    try {
      const res = await userAPI.getById(userId);
      if (isForbiddenError(res)) {
        setIsForbidden(true);
        return;
      }
      if (isNotFoundError(res)) {
        setIsNotFound(true);
        return;
      }
      if (res && !res.error && res.value) {
        setUser(res.value);

        // Look up client from context first, fallback to API
        if (res.value.clientId) {
          const contextClient = (clients || []).find((c) => c.id === res.value.clientId);
          if (contextClient) {
            setClient(contextClient);
          } else {
            try {
              const clientRes = await clientAPI.getById(res.value.clientId);
              if (clientRes && !clientRes.error && clientRes.value) {
                setClient(clientRes.value);
              }
            } catch {
              // Ignore client fetch errors
            }
          }
        }

        // Fetch notification preferences if user has notification categories
        const profile = res.value.profiles?.[0]?.code ?? 'USER';
        const cats = NOTIF_CATEGORIES[profile] ?? [];
        if (cats.length > 0) {
          const defaults = Object.fromEntries(cats.map((c) => [c, false]));
          setNotifPrefs(defaults);
          setNotifLoading(true);
          try {
            const prefsRes = await userAPI.getUserNotificationPreferences(userId);
            const map = { ...defaults };
            const prefsArray = prefsRes?.value ?? prefsRes ?? [];
            if (Array.isArray(prefsArray)) {
              prefsArray.forEach((p) => {
                map[p.category] = !!p.isEnabled;
              });
            }
            setNotifPrefs(map);
          } catch {
            // Ignore prefs fetch errors
          } finally {
            setNotifLoading(false);
          }
        }
      } else {
        toast.error(t('detail.loadError', 'Erro ao carregar detalhes do utilizador'));
      }
    } catch (err) {
      if (isForbiddenError(null, err)) {
        setIsForbidden(true);
      } else if (isNotFoundError(null, err)) {
        setIsNotFound(true);
      } else {
        toast.error(t('detail.loadError', 'Erro ao carregar detalhes do utilizador'));
      }
    } finally {
      setLoading(false);
    }
  }, [userId, clients, t]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    if (clients) loadUserData();
  }, [loadUserData, clients]);

  const handleTogglePreference = async (cat, isEnabled) => {
    // Optimistic UI update
    setNotifPrefs((prev) => ({ ...prev, [cat]: isEnabled }));
    try {
      const prefsPayload = Object.entries({ ...notifPrefs, [cat]: isEnabled })
        .map(([category, val]) => ({ category, isEnabled: val }));
      const res = await userAPI.updateUserNotificationPreferences(userId, prefsPayload);
      if (res?.error) {
        toast.error(t('detail.updateNotifError', 'Erro ao atualizar preferências de notificação'));
        // Revert UI
        setNotifPrefs((prev) => ({ ...prev, [cat]: !isEnabled }));
      } else {
        toast.success(t('detail.updateNotifSuccess', 'Preferência de notificação atualizada'));
      }
    } catch {
      toast.error(t('detail.updateNotifError', 'Erro ao atualizar preferências de notificação'));
      setNotifPrefs((prev) => ({ ...prev, [cat]: !isEnabled }));
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Breadcrumbs separator="›" sx={{ mb: 3 }}>
          <Skeleton variant="text" width={80} height={20} animation="wave" />
          <Skeleton variant="text" width={140} height={20} animation="wave" />
        </Breadcrumbs>

        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          <Skeleton variant="circular" width={44} height={44} animation="wave" />
          <Box sx={{ width: 250 }}>
            <Skeleton variant="text" width={180} height={28} animation="wave" />
            <Skeleton variant="text" width={120} height={18} animation="wave" />
          </Box>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Skeleton variant="text" width={100} height={20} animation="wave" sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {[1, 2, 3, 4].map((i) => (
                    <Box key={i}>
                      <Skeleton variant="text" width={80} height={16} animation="wave" />
                      <Skeleton variant="text" width={160} height={22} animation="wave" />
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Skeleton variant="text" width={140} height={22} animation="wave" sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rounded" width="100%" height={48} animation="wave" sx={{ borderRadius: 1 }} />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (isForbidden) return <AccessDeniedState backPath="/Gateway/Users" />;

  if (isNotFound || !user) return <NotFoundState backPath="/Gateway/Users" />;

  const profileCode = user.profiles?.[0]?.code ?? 'USER';
  const notifCategories = NOTIF_CATEGORIES[profileCode] ?? [];

  const getProfileLabel = (code) => {
    if (code === 'GATEWAY_ADMIN') return t('profiles.gatewayAdmin');
    if (code === 'CLIENT_ADMIN') return t('profiles.clientAdmin');
    return t('profiles.user');
  };

  const getStatusLabelAndColor = (status) => {
    if (status === 'A') return { label: t('detail.active', 'Ativo'), color: 'success' };
    if (status === 'P') return { label: t('detail.pending', 'Pendente'), color: 'warning' };
    return { label: t('detail.inactive', 'Inativo'), color: 'default' };
  };

  const statusInfo = getStatusLabelAndColor(user.sysStatus);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator="›" sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/Gateway/Users" sx={{ cursor: 'pointer', color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          {t('detail.title', 'Utilizadores')}
        </Link>
        <Typography color="text.primary" fontWeight={500}>{user.name}</Typography>
      </Breadcrumbs>

      {/* Header section */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '70%' }}>
          <IconButton onClick={() => navigate('/Gateway/Users')} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ bgcolor: '#1B75BB', width: 44, height: 44, fontSize: 18, fontWeight: 700 }}>
            {clientInitials(user.name)}
          </Avatar>
          <Box sx={{ width: '100%' }}>
            <Typography variant="h5" fontWeight={700}>{user.name}</Typography>
            <Typography variant="caption" color="text.secondary">{user.jobTitle || t('detail.noJobTitle', 'Sem cargo definido')}</Typography>
          </Box>
        </Stack>
      </Stack>

      {/* ─── Main Grid Layout ─── */}
      <Grid container spacing={3}>
        {/* Left Side: Avatar Card */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={2}>
                {t('detail.generalData', 'DADOS GERAIS')}
              </Typography>
              <Stack spacing={2}>
                {/* Estado */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <ShieldIcon color="action" fontSize="small" />
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{t('detail.status', 'Estado')}</Typography>
                    <Chip
                      label={statusInfo.label}
                      color={statusInfo.color}
                      size="small"
                      sx={{ fontWeight: 600, mt: 0.5 }}
                    />
                  </Box>
                </Stack>

                {/* Perfil */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <SettingsIcon color="action" fontSize="small" />
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{t('detail.profile', 'Perfil')}</Typography>
                    <Chip
                      label={getProfileLabel(profileCode)}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 500, mt: 0.5 }}
                    />
                  </Box>
                </Stack>

                <Divider />

                {/* Email */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <EmailIcon color="action" fontSize="small" />
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{t('detail.contactEmail', 'Email de contacto')}</Typography>
                    <Typography variant="body2">{user.email}</Typography>
                  </Box>
                </Stack>

                {/* Telefone */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <PhoneIcon color="action" fontSize="small" />
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{t('detail.phone', 'Telefone')}</Typography>
                    <Typography variant="body2">{user.phone || '—'}</Typography>
                  </Box>
                </Stack>

                {/* Cliente Associado */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <BusinessIcon color="action" fontSize="small" />
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{t('detail.associatedClient', 'Cliente associado')}</Typography>
                    {client ? (
                      <Link
                        component={RouterLink}
                        to={`/Gateway/Clients/${client.id}`}
                        underline="hover"
                        fontWeight={600}
                        variant="body2"
                      >
                        {client.name}
                      </Link>
                    ) : (
                      <Typography variant="body2" color="text.secondary">{t('detail.gateway', 'Gateway')}</Typography>
                    )}
                  </Box>
                </Stack>

                {/* Provedor de Identidade */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <SettingsIcon color="action" fontSize="small" />
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{t('detail.identityProvider', 'Provedor de Identidade')}</Typography>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {user.authProvider ? user.authProvider.toLowerCase() : 'local'}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side: Details Cards */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Notification Preferences Card */}
            {notifCategories.length > 0 && (
              <Card sx={{ border: 'none', boxShadow: 'none' }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={2}>
                    {t('detail.notifPreferencesTitle', 'PREFERÊNCIAS DE NOTIFICAÇÃO')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t('detail.notifPreferencesSubtitle', 'Configure os tipos de alertas automáticos por email que este utilizador deve receber do sistema.')}
                  </Typography>
                  <Stack spacing={2}>
                    {notifCategories.map((cat) => (
                      <Paper
                        key={cat}
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{ pr: 2 }}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: 13 }}>
                            {t(`notificationPreferences.categories.${cat}`)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('detail.notifPreferencesCategoryDesc', { category: cat.replace('_', ' ').toLowerCase() })}
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              size="medium"
                              checked={!!notifPrefs[cat]}
                              onChange={(e) => handleTogglePreference(cat, e.target.checked)}
                              disabled={notifLoading}
                            />
                          }
                          label=""
                          sx={{ m: 0 }}
                        />
                      </Paper>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default UserDetailPage;
